/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../_metronic/layout/core/PageData'
import {useClientDeleteByIdMutation, useClientGetByIdMutation} from '../Client.api'
import { DropdownMenu, SettingModalOption, SettingOption } from '../../../common/component/DropdownMenu'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { IClientDto } from '../Client.model'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { DeleteConfirmationModal } from '../../../common/component/modal/DeleteConfirmationModal'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { parseLocalDate } from '../../../util/Locale'
import { renderIdentifier } from '../../../util/Constants'
import { Spinner } from '../../../common/component/Spinner'
import { ContactEditor } from '../../contact/components/ContactEditor'

const ClientDetailsPage = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {clientId} = useParams()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.CLIENTS'}),
      path: '/clients',
      isSeparator: false,
      isActive: false,
    },
  ]
  const settingOptions: SettingOption[] = [
    {
      label: intl.formatMessage({id: 'UTIL.EDIT'}),
      icon: 'bi-pencil',
      action: () => navigate(`/clients/${clientId}/edit`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.CREATE.CONTRACT'}),
      icon: 'bi-vector-pen',
      action: () => navigate(`/contracts/new?clientId=${client?.id}`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.CREATE.ASSET'}),
      icon: 'bi-cpu',
      action: () => navigate(`/assets/new?clientId=${clientId}`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.CREATE.REPORT'}),
      icon: 'bi-speedometer',
      action: () => navigate(`/reports/new?clientId=${clientId}`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.CREATE.TICKET'}),
      icon: 'bi-wrench-adjustable-circle',
      action: () => navigate(`/tickets/new?clientId=${clientId}`),
    },
  ]
  const removeSettingOption: SettingModalOption = {
    label: intl.formatMessage({id: 'UTIL.DELETE'}),
    targetModal: '#client_delete_confirmation_modal'
  }

  const didFetchClient = useRef(false)
  const didRequestDelete = useRef(false)
 
  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()

  const [openDropDown, setOpenDropDown] = useState(false)
  const [isClientLoading, setIsClientLoading] = useState(true)
  const [client, setClient] = useState<IClientDto>()
  
  const [clientGetById] = useClientGetByIdMutation()
  const [clientDeleteById] = useClientDeleteByIdMutation()

  useEffect(() => {
    const fetchClient = async (clientId) => {
      try {
        if (!didFetchClient.current) {
          setIsClientLoading(true)
          const response: any = await clientGetById({id: clientId})
          if (!response || response.error) {
            setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
          } else {
            const client: IClientDto = response.data
            setClient(client)
          }
        }
      } catch (e: any) {
        setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
      } finally {
        setIsClientLoading(false)
      }
    }

    if (clientId) {
      fetchClient(clientId)
    }
  }, [])

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  const deleteConfirmOnClickHandler = async () => {
    try {
      if (clientId && !didRequestDelete.current) {
        setIsClientLoading(true)
        const response: any = await clientDeleteById(clientId)
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          navigate(`/clients`)
        }
      }
    } catch (e) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsClientLoading(false)
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <div className='d-flex flex-row'>
            <div className='d-flex flex-column flex-row-fluid'>
              <div className='d-flex flex-row flex-column-fluid'>
                <div className='d-flex flex-row-fluid flex-start'>
                  {client && (
                    <div className='d-flex flex-column'>
                      <span className='text-gray-800 fs-2 fw-bolder'>{client.displayName}</span>
                      <span className='fw-bolder text-primary me-1 fs-5'>
                        {renderIdentifier(client.sequence)}
                      </span>
                    </div>
                  )}
                </div>

                <div className='d-flex flex-row-auto flex-start ms-5'>
                  {client && (
                    <>
                      <button
                        className='btn btn-sm btn-light-primary me-2'
                        onClick={() => setOpenDropDown((prev) => !prev)}
                        disabled={isClientLoading}
                      >
                        <i className='bi bi-three-dots fs-5'></i>
                      </button>
                      <DropdownMenu
                        visible={openDropDown}
                        setOpenDropDown={setOpenDropDown}
                        settingOptions={settingOptions}
                        deleteOption={removeSettingOption}
                      ></DropdownMenu>
                    </>
                  )}

                  <button className='btn btn-sm btn-light' onClick={() => navigate(`/clients`)}>
                    <i className='bi bi-x-lg'></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {isClientLoading ? (
            <Spinner />
          ) : !client ? (
            <div className='row'>
              <div className='d-flex flex-row-fluid flex-start mt-10'>
                <span className='text-danger '>
                  <div className='d-flex flex-row justify-content-start align-items-center'>
                    <span className='fs-6 me-1'>
                      <i className='text-danger me-1 bi bi-exclamation-octagon' />
                      {intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'})}
                    </span>
                  </div>
                </span>
              </div>
            </div>
          ) : (
            <>
              <div>
                <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                  <li className='nav-item'>
                    <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                      {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                      {intl.formatMessage({id: 'UTIL.CONTACTS'})}
                    </a>
                  </li>
                </ul>
                <div className='tab-content mt-10' id='myTabContent'>
                  <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                    <div className='row mb-5'>
                      <SubSectionHeader
                        title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                        separator={false}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.CREATED_DATE'})}
                            value={parseLocalDate(
                              client.createdAt,
                              intl.formatMessage({id: 'I18N.LOCALE'})
                            )}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                            value={parseLocalDate(
                              client.updatedAt,
                              intl.formatMessage({id: 'I18N.LOCALE'})
                            )}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ID'})}
                            value={`${client.sequence}`}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.DISPLAY_NAME'})}
                            value={client.displayName}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.SECTOR'})}
                            value={client.sector}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>

                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.LOCATION'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                            value={
                              client.countryCode
                                ? intl.formatMessage({
                                    id: `DROPDOWN.COUNTRY.${client.countryCode}`,
                                  })
                                : ''
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                            value={client.address}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>

                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.METRICS'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.CONTRACTS'})}
                            value={`${client.totalContract || 0}`}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ASSETS'})}
                            value={`${client.totalAsset || 0}`}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.REPORTS'})}
                            value={`${client.totalReport || 0}`}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_2' role='tabpanel'>
                    <div className='row mb-5'>
                      {client.contacts && (
                        <div className='px-5'>
                          <div className='row mb-5'>
                            <ContactEditor data={client.contacts} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <DeleteConfirmationModal
                id='client_delete_confirmation_modal'
                title={intl
                  .formatMessage({id: 'UTIL.DELETE_CONFIRMATION.TITLE'})
                  .replace(/%s/g, intl.formatMessage({id: 'UTIL.CLIENT'}))}
                description={`${client?.displayName} - #${client?.sequence}`}
                confirmOnClickHandler={deleteConfirmOnClickHandler}
              ></DeleteConfirmationModal>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export {ClientDetailsPage}
